/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useState, useEffect, useContext } from "react";
import "react-datepicker/dist/react-datepicker.css";
import Filter from "../components/Filter";
import statementData from "../assets/reportStatements.json";
import legacyData from "../assets/legacy.json";
import locationData from "../assets/locations.json";
import { ResponsiveBar } from "@nivo/bar";
import fetchHelper from "../services/fetchHelper";
import { LayoutContext } from "../components/layout";

let m2d;

export const queryDateFormat = (date) => {
  let y = date.getFullYear();
  let m = (1 + date.getMonth()).toString().padStart(2, "0");
  let d = date.getDate().toString().padStart(2, "0");

  return [y, m, d].join("-");
};

const NewDeviceBar = (prp) => {
  const { where, onLoadingChange, period } = prp;
  const [data, setData] = useState([]);
  const [minValue, setMinValue] = useState(4);

  const setupData = (rawData) => {
    onLoadingChange(false);
    let minValue = 0;
    const data = rawData.map((item) => {
      Object.keys(item)
        .filter((key) => key.search("mfr") === 0)
        .map((key) => key.slice(3))
        .forEach((key) => {
          item[key.toUpperCase()] = item["mfr" + key] * -1;
        });

      Object.keys(item)
        .filter((key) => key.search("type") === 0)
        .map((key) => key.slice(4))
        .forEach((key) => {
          item[key.toUpperCase()] = item["type" + key];
        });

      return item;
    });

    data.reverse();

    console.log("DATA", data);
    setData(data);
    minValue = Math.ceil(minValue / 100) * 100;
    setMinValue((minValue + 100) * -1);
  };

  const poll = () => {
    if (!where) return false;
    const urlParams = {
      where: where,
    };
    const lS = window.localStorage.getItem("genToken");
    let url = "/patientmanagement/rest/interrogations/newPatientDeviceViewCannedReport?";
    // if (process.env.NODE_ENV === "development") url = "/reportStatementViewReport.json?";
    onLoadingChange(true);
    fetch(url + new URLSearchParams(urlParams), { headers: { token: JSON.parse(lS)["token"] } })
      .then((r) => {
        return r.json();
      })
      .then((j) => {
        setupData(j);
      })
      .catch((e) => {
        onLoadingChange(false);
        console.log(e);
      });
  };

  useEffect(() => {
    let t = JSON.stringify(data);
    if (t != m2d) m2d = t;
  }, [data]);

  useEffect(() => {
    if (where) {
      poll();
    }
  }, [where]);

  useEffect(() => {
    if (period === "totalAdded") {
      const rawData = [...data];
      for (let i = 0; i <= rawData.length; i++) {
        for (let j in rawData[i]) {
          if (["BIO", "BOSTON", "CRTD", "CRTP", "ICD", "ILR", "JUDE", "MED", "PACE"].indexOf(j) >= 0) {
            if (i > 0) {
              rawData[i][j] += rawData[i - 1][j];
            }
          }
        }
      }
      setData(rawData);
    } else if (period === "monthly") {
      poll();
    }
  }, [period]);

  useEffect(() => {
    setupData([
      {
        deviceTypes: [{ count: 2, type: "ILR" }],
        id: 5,
        interrogationStatus: [{ count: 1, type: "FINAL" }],
        month: "2020-MAY",
        unique: "1",
        view: "4",
      },
      {
        deviceTypes: [{ count: 2, type: "ILR" }],
        id: 1,
        interrogationStatus: [{ count: 1, type: "FINAL" }],
        month: "2020-APR",
        unique: "1",
        view: "1",
      },
      {
        deviceTypes: [{ count: 2, type: "ILR" }],
        id: 4,
        interrogationStatus: [{ count: 1, type: "FINAL" }],
        month: "2020-MAR",
        unique: "1",
        view: "1",
      },
      {
        deviceTypes: [{ count: 2, type: "ILR" }],
        id: 2,
        interrogationStatus: [{ count: 1, type: "FINAL" }],
        month: "2020-FEB",
        unique: "1",
        view: "4",
      },
      {
        deviceTypes: [{ count: 2, type: "ILR" }],
        id: 3,
        interrogationStatus: [{ count: 4, type: "FINAL" }],
        month: "2020-JAN",
        unique: "1",
        view: "1",
      },
    ]);
  }, []);

  return (
    <div className="dashboard-bar-container">
      <div className="dashboard-bar-content-container" style={{ height: 800 }}>
        <ResponsiveBar
          data={data}
          keys={["PACE", "ILR", "ICD", "CRTP", "CRTD", "BIO", "BOSTON", "JUDE", "MED"].reverse()}
          indexBy="month"
          margin={{ top: 50, right: 130, bottom: 90, left: 60 }}
          padding={0.3}
          colors1={{ scheme: "nivo" }}
          colors={(d) => {
            let cM = {
              PACE: "FFEFBE",
              ILR: "F1E0AB",
              ICD: "DDBE8F",
              CRTP: "4093A3",
              CRTD: "57BCCD",
              BIO: "81D6E4",
              BOSTON: "45A334",
              JUDE: "81D2E5",
              MED: "F1D6F4",
            };
            return "#" + cM[d.id];
          }}
          markers={[
            {
              axis: "y",
              value: 0,
              lineStyle: { strokeOpacity: 0.1, stroke: "#fff", strokeWidth: 10 },
              textStyle: { fill: "#008c98" },
              legend: "Device Type",
              legendPosition: "top-left",
              legendOrientation: "vertical",
              legendOffsetY: 30,
            },
            {
              axis: "y",
              value: 0,
              lineStyle: { stroke: "#fff", strokeWidth: 2 },
              textStyle: { fill: "#EE5D74" },
              legend: "Manufacturer",
              legendPosition: "bottom-left",
              legendOrientation: "vertical",
              legendOffsetY: 30,
            },
          ]}
          defs={[
            {
              id: "dots",
              type: "patternDots",
              background: "inherit",
              color: "#38bcb2",
              size: 4,
              padding: 1,
              stagger: true,
            },
            {
              id: "lines",
              type: "patternLines",
              background: "inherit",
              color: "#eed312",
              rotation: -45,
              lineWidth: 6,
              spacing: 10,
            },
          ]}
          labelFormat={(label) => {
            return (label + "").replace("-", "");
          }}
          fill={[
            { match: { id: "fries" }, id: "dots" },
            { match: { id: "sandwich" }, id: "lines" },
          ]}
          borderColor={{ from: "color", modifiers: [["darker", 1.6]] }}
          axisTop={{
            tickSize: 0,
            format: () => null,
            tickPadding: -50,
            tickRotation: 0,
            legend: "",
            legendPosition: "middle",
          }}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: -90,
            legend: "Month",
            legendPosition: "middle",
            legendOffset: 80,
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: "",
            legendPosition: "middle",
            legendOffset: -40,
            format: (v) => (v + "").replace("-", ""),
          }}
          labelSkipWidth={12}
          labelSkipHeight={12}
          labelTextColor={(data) => {
            const cM = { "30 Day": "fff", "90 Day": "303030", "30&90 Day": "606060" };
            return "#" + cM[data.data.id];
          }}
          legends={[
            {
              dataFrom: "keys",
              anchor: "bottom-right",
              direction: "column",
              justify: false,
              translateX: 120,
              translateY: 0,
              itemsSpacing: 2,
              itemWidth: 100,
              itemHeight: 20,
              itemDirection: "left-to-right",
              itemOpacity: 0.85,
              symbolSize: 20,
              effects: [{ on: "hover", style: { itemOpacity: 1 } }],
            },
          ]}
          // tooltip={(tooltip) => {
          //   return (
          //     <div style={{ display: "flex", flexDirection: "column" }}>
          //       <span>
          //         {tooltip.id === "unique" ? "Patients" : tooltip.id}: {(tooltip.value + "").replace("-", "")}
          //       </span>
          //       {tooltip.value < 0 ? <span>Total Statements: {tooltip.data.view}</span> : null}
          //       {tooltip.value < 0 && tooltip.data.uniqueInterrogation ? <span>Reports: {tooltip.data.uniqueInterrogation}</span> : null}
          //     </div>
          //   );
          // }}
          animate={true}
          motionStiffness={90}
          motionDamping={15}
        />
        <div className="bar-label-container">
          <span>New Devices Added</span>
        </div>
      </div>
    </div>
  );
};

const NewDevice = () => {
  const { user } = useContext(LayoutContext);
  const [filter, setFilter] = useState({});
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);

  // const fetchLocation = async () => {
  //   try {
  //     let url = "/patientmanagement/rest/location/serviceReport";
  //     // if (process.env.NODE_ENV === "development") url = "/serviceReport.json";
  //     const res = await fetchHelper.get({ url });
  //     setLocations(res.map((i) => ({ value: i.id, label: i.name })));
  //   } catch (err) {
  //     console.log(err);
  //     setLocations([{ value: user?.locations[0]?.id, label: user?.locations[0]?.name }]);
  //   }
  // };

  useEffect(() => {
    if (user) {
      setLocations([{ value: user?.locations[0]?.id, label: user?.locations[0]?.name }]);
    }
  }, [user]);

  const onChangeFilter = (filter) => {
    if (locations.length > 0) setFilter(filter);
  };

  return (
    <div style={{ display: "flex", flexDirection: "row" }}>
      <Filter
        key={locations.length + "filter"}
        onChange={onChangeFilter}
        loading={loading}
        fields={[
          { type: "line" },
          {
            type: "multiSelect",
            key: "period",
            label: "Count",
            options: [
              { value: "monthly", label: "Monthly Added" },
              { value: "totalAdded", label: "Total Added" },
            ],
            maxSelect: 1,
            forceHasSelectAll: false,
            initialSelected: [{ value: "monthly" }],
          },
          {
            type: "multiSelect",
            whereKey: "location.id",
            key: "location",
            operator: "$eq",
            label: "Location",
            options: locations,
            maxSelect: 1,
            forceHasSelectAll: false,
            initialSelected: [{ value: user?.locations[0]?.id }],
          },
        ]}
      />
      <NewDeviceBar where={filter.where} onLoadingChange={(v) => setLoading(v)} period={filter.period} />
    </div>
  );
};

export default NewDevice;
